/**
 * Thin fetch wrapper. In dev, relative /api paths are proxied to localhost:8080
 * via Vite. In production, VITE_API_URL prefixes all requests to the CF Worker.
 */
const API_BASE = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
    message: string,
  ) {
    super(message);
  }
}

let _getToken: (() => Promise<string>) | null = null;
export function setTokenGetter(fn: () => Promise<string>) { _getToken = fn; }

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const authHeader: Record<string, string> = {};
  if (_getToken) {
    try { authHeader["Authorization"] = `Bearer ${await _getToken()}`; } catch { /* unauthenticated */ }
  }
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...authHeader,
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.text();
    let parsed: unknown = body;
    try {
      parsed = JSON.parse(body);
    } catch {
      /* keep string */
    }
    throw new ApiError(res.status, parsed, `${res.status} ${res.statusText}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// === Types (mirror services/sync-go + agents-py contracts) ================

export interface Health {
  status: "ok" | "degraded" | "down";
  shopify_env: "mock" | "production";
  db: "ok" | "down";
  uptime_seconds: number;
  shopify_admin_url: string; // e.g. "https://admin.shopify.com/store/oncloudtech"
}

/** Build a direct link to a product in Shopify Admin. */
export function shopifyAdminProductURL(adminBase: string, shopifyProductId: string | number | null): string | null {
  if (!adminBase || !shopifyProductId) return null;
  return `${adminBase}/products/${shopifyProductId}`;
}

export type CuratedState = "wip" | "ready" | "draft" | "published" | "archived";

export interface TdsynnexProduct {
  tdsynnex_sku: string;
  trading_partner_code: string;
  manufacturer_part_number: string;
  upc_code: string | null;
  manufacturer_name: string;
  brand_slug: string;
  short_description: string;
  contract_price: string | null;
  msrp: string | null;
  map_price: string | null;
  qty_on_hand: number;
  status: string;
  unspsc_code: string | null;
  category_code: string | null;
  sku_attributes: string | null;
  warehouse_qty: Record<string, number> | null;
  margin_pct: number | null;
  ship_weight: number | null;
  length: number | null;
  width: number | null;
  height: number | null;
  parcel_shippable: string | null;
  curated_id: string | null;
  curated_state: CuratedState | null;
  shopify_product_id: string | null;
}

export interface CompetitorPrice {
  name: string;
  url: string;
  price: number;
}

export interface CuratedProduct {
  id: string;
  tdsynnex_sku: string;
  title: string | null;
  seo_title: string | null;
  seo_description: string | null;
  body_html: string | null;
  tags: string[] | null;
  vendor: string | null;
  product_type: string | null;
  status: "wip" | "ready" | "draft" | "published" | "archived";
  shopify_product_id: string | null;
  updated_at: string;
  proposed_price: string | null;
  compare_at_price: string | null;
  competitor_prices: CompetitorPrice[];
  qty_override: number | null;
}

export interface PricingProposal {
  tdsynnex_sku: string;
  cost: number;
  floor: number;
  target: number;
  ceiling: number;
  proposed: number;
  map_enforced: boolean;
  margin_pct: number;
  min_margin: number;
  target_margin: number;
  rule_id?: string;
}

export interface ProductListResponse {
  total: number;
  items: TdsynnexProduct[];
}

export interface BrandListEntry {
  brand_slug: string;
  list_name: string;
  notes: string;
  added_at: string;
}

export interface BrandOption {
  slug: string;
  total: number;
  in_stock: number;
}

export interface ShopifyCatalogProduct {
  shopify_id: string;
  title: string;
  shopify_status: "ACTIVE" | "DRAFT" | "ARCHIVED";
  shopify_price: string;
  variant_sku: string;
  has_image: boolean;
  is_duplicate: boolean;
  local_id: string | null;
  local_state: string | null;
  local_price: string | null;
  local_title: string | null;
  drift: "price" | "status" | null;
}

export interface ImportConflict {
  curated_id: string;
  tdsynnex_sku: string;
  shopify_product_id: string;
  title: string;
  local_price: string;
  shopify_price: string;
  local_state: string;
  shopify_status: string;
}

export interface StoreProduct {
  id: string;
  title: string;
  tdsynnex_sku: string;
  brand_slug: string;
  state: CuratedState;
  shopify_product_id: string | null;
  shopify_status: "ACTIVE" | "DRAFT" | "ARCHIVED" | null;
  shopify_title: string | null;
  shopify_vendor: string | null;
  shopify_product_type: string | null;
  shopify_tags: string[] | null;
  shopify_body_html: string | null;
  shopify_seo_title: string | null;
  shopify_seo_description: string | null;
  local_price: string | null;
  shopify_price: string | null;
  shopify_compare_at_price: string | null;
  shopify_inventory_qty: number | null;
  shopify_weight: number | null;
  shopify_weight_unit: string | null;
  qty_on_hand: number;
  drift: "price" | "status" | "missing" | null;
  published_at: string | null;
  image_count: number;
}

export interface ProductImage {
  id: string;
  curated_id: string;
  source: "icecat" | "manual" | "shopify";
  url: string;
  filename: string | null;
  alt: string | null;
  position: number;
  width: number | null;
  height: number | null;
  file_size_bytes: number | null;
  shopify_image_id: number | null;
  created_at: string;
}

export interface IntegrationSetting {
  value?: string;
  configured?: boolean;
  description: string;
  updated_at: string;
}

export interface CatalogHealth {
  total_skus: number;
  curated: number;
  published: number;
  needs_image: number;
  coverage_pct: string;
}

export interface SyncHealth {
  total_published: number;
  drifted_price: number;
  drifted_status: number;
  missing_shopify: number;
  last_bulk_sync_at: string | null;
  needs_attention_count: number;
  needs_attention: Array<{
    id: string;
    title: string;
    sku: string;
    last_synced_at: string | null;
  }>;
}

export interface SyncRun {
  run_id: string;
  total: number;
}

export interface PricingRule {
  id: string;
  name: string;
  scope_brand: string | null;
  scope_category: string | null;
  min_margin: number;
  target_margin: number;
  rounding: string | null;
  active: boolean;
  version: string | null;
}

export interface ShopifyCollection {
  id: number;
  gid: string;
  title: string;
  handle: string;
}

export interface UploadBatch {
  id: string;
  filename: string;
  size_bytes: number;
  status: "uploading" | "parsing" | "completed" | "failed";
  rows_processed: number;
  started_at: string;
  completed_at: string | null;
  error: string | null;
}

// === Endpoints ============================================================

export const api = {
  health: () => request<Health>("/api/health"),

  brands: (q?: string) => {
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    return request<{ items: BrandOption[] }>(`/api/brands?${qs.toString()}`);
  },

  products: (params: {
    brand?: string | string[];
    sku_q?: string;
    mpn_q?: string;
    desc_q?: string;
    in_stock?: boolean;
    has_map?: boolean;
    min_qty?: string;
    max_qty?: string;
    min_cost?: string;
    max_cost?: string;
    min_map?: string;
    max_map?: string;
    min_margin?: string;
    max_margin?: string;
    unspsc_prefix?: string;
    preset?: "profitable" | "low_inventory" | "ready_to_list";
    curated_state?: "uncurated" | CuratedState;
    sort?: string;
    limit?: number;
    offset?: number;
  } = {}) => {
    const qs = new URLSearchParams();
    if (params.brand) {
      const brands = Array.isArray(params.brand) ? params.brand : [params.brand];
      brands.forEach((b) => qs.append("brand", b));
    }
    if (params.sku_q) qs.set("sku_q", params.sku_q);
    if (params.mpn_q) qs.set("mpn_q", params.mpn_q);
    if (params.desc_q) qs.set("desc_q", params.desc_q);
    if (params.in_stock) qs.set("in_stock", "true");
    if (params.has_map) qs.set("has_map", "true");
    if (params.min_qty) qs.set("min_qty", params.min_qty);
    if (params.max_qty) qs.set("max_qty", params.max_qty);
    if (params.min_cost) qs.set("min_cost", params.min_cost);
    if (params.max_cost) qs.set("max_cost", params.max_cost);
    if (params.min_map) qs.set("min_map", params.min_map);
    if (params.max_map) qs.set("max_map", params.max_map);
    if (params.min_margin) qs.set("min_margin", params.min_margin);
    if (params.max_margin) qs.set("max_margin", params.max_margin);
    if (params.unspsc_prefix) qs.set("unspsc_prefix", params.unspsc_prefix);
    if (params.preset) qs.set("preset", params.preset);
    if (params.curated_state) qs.set("curated_state", params.curated_state);
    if (params.sort) qs.set("sort", params.sort);
    qs.set("limit", String(params.limit ?? 50));
    qs.set("offset", String(params.offset ?? 0));
    return request<ProductListResponse>(`/api/products?${qs.toString()}`);
  },

  patchCuratedState: (id: string, state: CuratedState) =>
    request<{ id: string; state: CuratedState }>(`/api/curated/${id}/state`, {
      method: "PATCH",
      body: JSON.stringify({ state }),
    }),

  productDetail: (sku: string) =>
    request<TdsynnexProduct>(`/api/products/${encodeURIComponent(sku)}`),

  curate: (sku: string) =>
    request<CuratedProduct>(`/api/curate/${encodeURIComponent(sku)}`, {
      method: "POST",
    }),

  curatedList: (params: { status?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set("status", params.status);
    return request<{ items: CuratedProduct[] }>(
      `/api/curated?${qs.toString()}`,
    );
  },

  curatedDetail: (id: string) => request<CuratedProduct>(`/api/curated/${id}`),

  curatedUpdate: (id: string, patch: Partial<CuratedProduct>) =>
    request<CuratedProduct>(`/api/curated/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  publish: (id: string) =>
    request<{ shopify_product_id: string; status: string }>(
      `/api/curated/${id}/publish`,
      { method: "POST", headers: { "X-Confirm-Production": "yes" } },
    ),

  delist: (id: string) =>
    request<{ delisted: boolean; shopify: string }>(`/api/curated/${id}/delist`, { method: "POST" }),

  pricingPropose: (sku: string) =>
    request<PricingProposal>(`/api/pricing/propose`, {
      method: "POST",
      body: JSON.stringify({ tdsynnex_sku: sku }),
    }),

  curateDraft: (sku: string) =>
    request<{ found: boolean; draft: Omit<CuratedProduct, "id" | "tdsynnex_sku" | "status" | "shopify_product_id" | "updated_at" | "competitor_prices"> | null }>(
      `/api/curate/${encodeURIComponent(sku)}/draft`,
    ),

  curateCommit: (sku: string, draft: { title: string; body_html: string; seo_title: string; seo_description: string; tags: string[] }) =>
    request<{ id: string; state: string; title: string }>(`/api/curate/${encodeURIComponent(sku)}/commit`, {
      method: "POST",
      body: JSON.stringify(draft),
    }),

  competitorsScan: (sku: string) =>
    request<{ started: boolean; job_id: string }>(`/api/competitors/scan`, {
      method: "POST",
      body: JSON.stringify({ tdsynnex_sku: sku }),
    }),

  batches: () => request<{ items: UploadBatch[] }>(`/api/batches`),

  storeProducts: () => request<{ items: StoreProduct[] }>("/api/store/products"),

  storeSync: (id: string) =>
    request<{ synced: boolean; operation: string }>(`/api/store/sync/${id}`, {
      method: "POST",
    }),

  storePull: (id: string) =>
    request<{ pulled: boolean; operation: string; changes: string[] }>(`/api/store/pull/${id}`, {
      method: "POST",
    }),

  pullAllFromShopify: () =>
    request<{ matched: number; missing: number; total: number }>(`/api/store/pull-all`, { method: "POST" }),

  bulkImageSync: () =>
    request<{ total: number; synced: number; skipped: number; failed: number }>(`/api/store/images/pull-all`, { method: "POST" }),

  storeImport: () =>
    request<{
      linked: number; unmatched: number;
      conflicts: ImportConflict[];
      items: Array<{ tdsynnex_sku: string; shopify_product_id: string; action: string }>;
    }>(`/api/store/import`, { method: "POST" }),

  storeImportResolve: (resolutions: Array<{
    curated_id: string; shopify_product_id: string;
    shopify_price: string; shopify_status: string; keep: "shopify" | "local";
  }>) =>
    request<{ applied: number }>(`/api/store/import/resolve`, {
      method: "POST",
      body: JSON.stringify({ resolutions }),
    }),

  shopifyCatalog: () =>
    request<{ products: ShopifyCatalogProduct[]; total: number }>("/api/store/shopify/catalog"),

  claimShopifyProduct: (shopify_product_id: string) =>
    request<{ id: string; title: string; already_claimed?: boolean }>("/api/store/claim", {
      method: "POST",
      body: JSON.stringify({ shopify_product_id }),
    }),

  unmatchedSetStatus: (shopify_product_id: string, status: "DRAFT" | "ARCHIVED") =>
    request<{ ok: boolean; status: string }>("/api/store/unmatched/status", {
      method: "POST",
      body: JSON.stringify({ shopify_product_id, status }),
    }),

  // Product images
  listImages: (curatedId: string) =>
    request<{ items: ProductImage[] }>(`/api/curated/${curatedId}/images`),

  uploadImageFile: (curatedId: string, file: File, alt?: string) => {
    const form = new FormData();
    form.append("image", file);
    if (alt) form.append("alt", alt);
    return fetch(`/api/curated/${curatedId}/images`, {
      method: "POST",
      body: form,
    }).then(async (res) => {
      if (!res.ok) throw new ApiError(res.status, await res.text(), `${res.status}`);
      return res.json() as Promise<ProductImage>;
    });
  },

  uploadImageUrl: (curatedId: string, url: string, alt?: string, source?: "icecat" | "manual") =>
    request<ProductImage>(`/api/curated/${curatedId}/images`, {
      method: "POST",
      body: JSON.stringify({ url, alt, source: source ?? "manual" }),
    }),

  deleteImage: (curatedId: string, imageId: string) =>
    request<void>(`/api/curated/${curatedId}/images/${imageId}`, { method: "DELETE" }),

  reorderImages: (curatedId: string, order: string[]) =>
    request<{ reordered: number }>(`/api/curated/${curatedId}/images/reorder`, {
      method: "PUT",
      body: JSON.stringify({ order }),
    }),

  // Integration settings
  getSettings: () =>
    request<Record<string, IntegrationSetting>>(`/api/settings`),

  putSetting: (key: string, value: string) =>
    request<{ key: string; updated: boolean }>(`/api/settings/${encodeURIComponent(key)}`, {
      method: "PUT",
      body: JSON.stringify({ value }),
    }),

  catalogHealth: () => request<CatalogHealth>(`/api/health/catalog`),

  syncHealth: () => request<SyncHealth>(`/api/store/health`),

  matchFromShopify: () =>
    request<{ shopify_products: number; local_products: number; matched: number; already_correct: number; unmatched: number }>(
      `/api/store/sync/match`, { method: "POST" }
    ),

  bulkSync: () =>
    request<SyncRun>(`/api/store/sync/bulk`, { method: "POST" }),

  listPricingRules: () =>
    request<{ items: PricingRule[] }>(`/api/pricing/rules`),

  createPricingRule: (rule: Omit<PricingRule, "id" | "version">) =>
    request<{ id: string }>(`/api/pricing/rules`, {
      method: "POST",
      body: JSON.stringify(rule),
    }),

  patchPricingRule: (id: string, patch: Partial<PricingRule>) =>
    request<{ id: string; updated: boolean }>(`/api/pricing/rules/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  deletePricingRule: (id: string) =>
    request<void>(`/api/pricing/rules/${id}`, { method: "DELETE" }),

  // Shopify collections
  listCollections: () =>
    request<{ items: ShopifyCollection[] }>("/api/shopify/collections"),

  syncCollections: () =>
    request<{ synced: number }>("/api/shopify/collections/sync", { method: "POST" }),

  getCuratedCollections: (curatedId: string) =>
    request<{ items: ShopifyCollection[] }>(`/api/curated/${curatedId}/collections`),

  updateCuratedCollections: (curatedId: string, collectionIds: number[]) =>
    request<{ updated: number }>(`/api/curated/${curatedId}/collections`, {
      method: "PUT",
      body: JSON.stringify({ collection_ids: collectionIds }),
    }),

  // Brand list management
  brandLists: () =>
    request<{ preferred: BrandListEntry[]; mvp: BrandListEntry[]; excluded: BrandListEntry[] }>(
      `/api/brand-lists`,
    ),

  addToBrandList: (brandSlug: string, listName: "preferred" | "mvp" | "excluded", notes?: string) =>
    request<{ status: string }>(`/api/brand-lists`, {
      method: "POST",
      body: JSON.stringify({ brand_slug: brandSlug, list_name: listName, notes: notes ?? "" }),
    }),

  removeFromBrandList: (brandSlug: string, listName: string) =>
    request<{ status: string }>(`/api/brand-lists/${brandSlug}/${listName}`, {
      method: "DELETE",
    }),
};
